import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks.ts";
import {
  clearAuthenticationSession,
  hasVersionChanged,
  LOGIN_API_VERSION_KEY,
  LOGIN_UI_VERSION_KEY,
  normalizeVersion,
} from "features/auth/authSession.ts";
import { removeCredentials, selectUser } from "features/auth/authSlice.ts";
import { useGetEnvironmentQuery } from "features/environment/environmentApiSlice.ts";
import { removePermissions } from "features/permissions/permissionsSlice.ts";

const VERSION_CHECK_INTERVAL_MS = 60_000;

const VersionChangeHandler = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const reloadStartedRef = useRef(false);
  const { data: environment } = useGetEnvironmentQuery(undefined, {
    pollingInterval: VERSION_CHECK_INTERVAL_MS,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: !user,
  });

  const logoutAndReload = useCallback(() => {
    if (reloadStartedRef.current) return;

    reloadStartedRef.current = true;
    clearAuthenticationSession();
    dispatch(removeCredentials());
    dispatch(removePermissions());
    window.location.reload();
  }, [dispatch]);

  useEffect(() => {
    if (!user) return;

    const loginUiVersion = localStorage.getItem(LOGIN_UI_VERSION_KEY);
    if (!loginUiVersion) {
      localStorage.setItem(LOGIN_UI_VERSION_KEY, APP_VERSION);
    } else if (hasVersionChanged(loginUiVersion, APP_VERSION)) {
      logoutAndReload();
    }
  }, [logoutAndReload, user]);

  useEffect(() => {
    if (!user) return;

    const currentApiVersion = normalizeVersion(environment?.apiVersion);
    if (!currentApiVersion) return;

    const loginApiVersion = localStorage.getItem(LOGIN_API_VERSION_KEY);
    if (!loginApiVersion) {
      localStorage.setItem(LOGIN_API_VERSION_KEY, currentApiVersion);
    } else if (hasVersionChanged(loginApiVersion, currentApiVersion)) {
      logoutAndReload();
    }
  }, [environment?.apiVersion, logoutAndReload, user]);

  return null;
};

export default VersionChangeHandler;
